import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DeleteIcon from '@src/svgs/DeleteIcon';
import EyeClosedIcon from '@src/svgs/EyeClosedIcon';
import EyeOpenIcon from '@src/svgs/EyeOpenIcon';
import ReplaceIcon from '@src/svgs/ReplaceIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import TranscriptIcon from '@src/svgs/TranscriptIcon';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { CLIP_PREVIEW_Z } from '../clipPreviewZ';

/** Where a right-click landed, and on which Section. */
export type ReelSectionMenuTarget = {
  sectionId: string;
  x: number;
  y: number;
};

/** Wide enough for the longest label; a pointer position cannot set this. */
const MENU_WIDTH = 200;

/** One item: 8px padding either side of a 15px line, plus the 2px row gap. */
const MENU_ITEM_HEIGHT = 35;

/** The surface's own padding, top and bottom. */
const MENU_PADDING = 8;

/**
 * How long Turn Clip On/Off holds its gold flash before the menu closes and
 * the edit runs. A blink — long enough to be seen, short enough that the menu
 * is never in the way of watching the row change.
 */
export const TOGGLE_FLASH_MS = 120;

interface ReelSectionContextMenuProps {
  target: ReelSectionMenuTarget;
  onClose: () => void;
  /**
   * Slot 0 — opens the row's own title field (PLAT-11397). The rename used to
   * be reachable only by clicking the title, which nothing on the row said out
   * loud once the hover pencil came off it: the panel's name column is ~140px
   * and the pencil was spending its last 10px. So the affordance moved here,
   * where the Chapter heading's Edit Name already is — same label, same glyph,
   * same place in the list.
   */
  onRename?: (sectionId: string) => void;
  /**
   * Slot 1 — names the Section from what is said in it (PLAT-11059). Above Edit
   * Transcript, the position PLAT-11060 DS3 left for it: the menu runs navigate
   * → change what the Section says → set it aside → destroy, and rewriting the
   * label is lighter than all four.
   */
  onGenerateName?: (sectionId: string) => void;
  /**
   * PLAT-11059 — a name is already being generated for this Section. Slot 0
   * greys out rather than queueing a second request; the spinner is on the row,
   * where the name is (DS6).
   */
  isGeneratingName?: boolean;
  /**
   * Slot 1 — opens the Transcript tab on this Section's words (PLAT-11061).
   * Absent when this clip preview has no transcript tab to send them to.
   */
  onEditTranscript?: (sectionId: string) => void;
  /**
   * Slot 2 — swaps this Section's passage for a different clip, in place
   * (PLAT-11071). Opens the picker; the menu closes first (PLAT-11071 DS6).
   */
  onReplace?: (sectionId: string) => void;
  /**
   * Slot 3 — takes the Section out of the reel without losing it, or puts it
   * back (PLAT-11072). One item, two labels: the state is already legible from
   * the row's own dimming, so the item only names the action available (DS6).
   */
  onToggleEnabled?: (sectionId: string) => void;
  /**
   * PLAT-11072 — whether this Section is in the reel. Decides slot 3's label and
   * icon, and greys out the two slots above it: neither Edit Transcript nor
   * Replace with Clip has a passage in the timeline to act on (DS7).
   */
  isEnabled?: boolean;
  /**
   * False renders slot 3 disabled — the last Section still on cannot be turned
   * off, for the reason `canRemove` exists (DS5).
   */
  canDisable?: boolean;
  /** Why turning this Section off is unavailable, as its native tooltip. */
  disableDisabledReason?: string;
  /**
   * Slot 4 — drops the Section out of the reel (DS8: not the source clip).
   * Absent callback, absent item, which is how the three later slots arrive
   * (PLAT-11061 / 11071 / 11072) without touching each other.
   */
  onRemove?: (sectionId: string) => void;
  /**
   * False renders Remove disabled rather than dropping it (DS10) — a menu that
   * silently loses an item on a one-Section reel reads as a bug.
   */
  canRemove?: boolean;
  /** Why Remove is unavailable, as its native tooltip. */
  removeDisabledReason?: string;
}

/** Why the two passage-level actions stand down on a Section that is off. */
const OFF_SECTION_REASON = 'This clip is turned off';

/** Why Generate Name stands down while this Section's own run is in flight. */
const GENERATING_NAME_REASON = 'Already generating a name for this clip';

/**
 * PLAT-11060 — the Section row's action surface (DS1), and the menu every
 * per-Section action in this epic opens on.
 *
 * One menu for the whole list, positioned from the pointer (DS2): the panel
 * holds which Section it belongs to and where the click landed, so no row
 * carries open/closed state or a portal of its own and `ReelSectionRow`'s memo
 * survives a drag.
 *
 * Portaled to `document.body` — the Section list scrolls and the panel sits
 * inside the clip-preview modal, so an in-panel menu would be clipped by the one
 * and stacked under the other.
 *
 * `useAnchoredDropdown` is not usable here: it positions from a trigger
 * element's rect, and a right-click has a point rather than a trigger. So this
 * owns the four things the hook would otherwise have given it — outside click,
 * Escape, dismiss on a scroll gesture, and a z-tier that clears the clip-preview
 * stack.
 */
export const ReelSectionContextMenu: React.FC<ReelSectionContextMenuProps> = ({
  target,
  onClose,
  onRename,
  onGenerateName,
  isGeneratingName = false,
  onEditTranscript,
  onReplace,
  onToggleEnabled,
  isEnabled = true,
  canDisable = true,
  disableDisabledReason,
  onRemove,
  canRemove = true,
  removeDisabledReason,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  // Turn Clip On/Off has been clicked and is showing its flash. Also the
  // guard against a second click landing inside it.
  const [isTogglePicked, setIsTogglePicked] = useState(false);
  const flashTimer = useRef<number | null>(null);

  // Dismissing the menu mid-flash cancels the edit with it: Escape and an
  // outside click mean "never mind", and a toggle that fired anyway a moment
  // after the menu vanished would be the creator's change, uninvited.
  useEffect(
    () => () => {
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  // The shared hook, never a hand-rolled `document.addEventListener('mousedown')`
  // (CLAUDE.md): it also treats focus moving into the renderer iframe as an
  // outside click, which a `contains()` check on its own misses.
  useOutsideAlerter(menuRef, onClose);

  useEffect(() => {
    // Escape dismisses the menu and **nothing else**. Capture phase, so this
    // runs before the clip-preview modal's own `document` keydown — which would
    // otherwise take the whole clip preview down with the menu, the exact bug
    // `useAnchoredDropdown` documents in its Escape handler. Capture rather than
    // bubble because both listeners sit on `document`, where registration order
    // would decide it: the modal's is registered first, so a bubble listener
    // here would fire second and stop propagation too late.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    // The menu is `position: fixed` and the Section list scrolls under it, so a
    // held-open menu would detach from its row. The *gesture* rather than the
    // `scroll` event it produces: a row's transcript fold opening or collapsing
    // resizes the list under an open menu, and Chrome's scroll anchoring
    // compensates for that by adjusting `scrollTop` — which fires `scroll` with
    // no scroll behind it. Listening for that dismissed the menu a moment after
    // it opened, while the pointer was still on its way to an item (PLAT-11188).
    // Capture, so a wheel over any scroller counts and not only the window's.
    const onUserScroll = () => onClose();

    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('wheel', onUserScroll, true);
    window.addEventListener('touchmove', onUserScroll, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('wheel', onUserScroll, true);
      window.removeEventListener('touchmove', onUserScroll, true);
    };
  }, [onClose]);

  const items = [
    onRename,
    onGenerateName,
    onEditTranscript,
    onReplace,
    onToggleEnabled,
    onRemove,
  ].filter(Boolean).length;
  const { x, y } = clampMenuToViewport(
    target.x,
    target.y,
    MENU_WIDTH,
    items * MENU_ITEM_HEIGHT + MENU_PADDING,
  );

  /** Closes before it acts, the ordering `InsertDivider`'s menu takes. */
  const pick = (action: () => void) => {
    onClose();
    action();
  };

  return createPortal(
    <ContextMenuSurface
      ref={menuRef}
      role="menu"
      data-testid="reel-section-context-menu"
      style={{
        top: y,
        left: x,
        width: MENU_WIDTH,
        // `ContextMenuSurface`'s resting z sits *below* the clip-preview modal
        // stack, so without this the menu opens behind the modal it lives in.
        zIndex: CLIP_PREVIEW_Z.reelSectionContextMenu,
      }}
    >
      {onRename && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          // Live on a Section that is off, unlike the three slots below it: a
          // clip set aside still has a name, and renaming it is how a creator
          // labels what they are keeping for later (PLAT-11072 DS7).
          data-testid="reel-section-menu-rename"
          onClick={() => pick(() => onRename(target.sectionId))}
        >
          {/* The glyph the row's title used to wear on hover, said out loud —
              the same pairing the Chapter heading's Edit Name uses. */}
          <SimplePencilIcon width="14" height="14" />
          Edit Name
        </ContextMenuItem>
      )}
      {onGenerateName && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          // Off, the passage is out of the timeline and there are no words to
          // read — the same reading the two slots below take (PLAT-11072 DS7).
          // While a name is already generating, the item stands down rather than
          // queueing a second request for the same row.
          $disabled={!isEnabled || isGeneratingName}
          title={
            !isEnabled
              ? OFF_SECTION_REASON
              : isGeneratingName
                ? GENERATING_NAME_REASON
                : undefined
          }
          aria-disabled={!isEnabled || isGeneratingName || undefined}
          data-testid="reel-section-menu-generate-name"
          onClick={() => {
            if (!isEnabled || isGeneratingName) return;
            pick(() => onGenerateName(target.sectionId));
          }}
        >
          {/* Sized and coloured like the other items' glyphs — the icon's own
              defaults are a 10px green sparkle built for a different surface. */}
          <SparkleIcon width="14" height="14" strokeColor="currentColor" />
          Generate Name with AI
        </ContextMenuItem>
      )}
      {onEditTranscript && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          // Off, there is no passage in the timeline to jump to — so the item
          // greys out rather than disappearing, the same reading Remove takes
          // on a one-Section reel (PLAT-11072 DS7).
          $disabled={!isEnabled}
          title={isEnabled ? undefined : OFF_SECTION_REASON}
          aria-disabled={!isEnabled || undefined}
          data-testid="reel-section-menu-edit-transcript"
          onClick={() => {
            if (!isEnabled) return;
            pick(() => onEditTranscript(target.sectionId));
          }}
        >
          {/* The Transcript tab's own glyph, so the item names its destination
              in the same way the tab does. */}
          <TranscriptIcon />
          Edit Transcript
        </ContextMenuItem>
      )}
      {onReplace && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          // …and nothing in the timeline for a swap to take the place of.
          $disabled={!isEnabled}
          title={isEnabled ? undefined : OFF_SECTION_REASON}
          aria-disabled={!isEnabled || undefined}
          data-testid="reel-section-menu-replace"
          onClick={() => {
            if (!isEnabled) return;
            pick(() => onReplace(target.sectionId));
          }}
        >
          <ReplaceIcon strokeColor="currentColor" />
          Replace with Clip
        </ContextMenuItem>
      )}
      {onToggleEnabled && (
        <ToggleItem
          type="button"
          role="menuitem"
          // Only the turning-*off* direction can be unavailable: a reel has to
          // keep one Section playing (DS5). Turning one back on never can.
          $disabled={isEnabled && !canDisable}
          title={isEnabled && !canDisable ? disableDisabledReason : undefined}
          aria-disabled={(isEnabled && !canDisable) || undefined}
          data-testid="reel-section-menu-toggle-enabled"
          data-picked={isTogglePicked || undefined}
          $picked={isTogglePicked}
          onClick={() => {
            if ((isEnabled && !canDisable) || isTogglePicked) return;
            // Gold on the click, gone a blink later: what this item changes is
            // a row *behind* the menu, so without the flash the click has
            // nothing to acknowledge it (PLAT-11072).
            setIsTogglePicked(true);
            flashTimer.current = window.setTimeout(() => {
              flashTimer.current = null;
              onClose();
              // The edit is a timeline rebuild, long enough that running it in
              // the same task as the close would batch the two — the menu would
              // then sit there, gold, for the whole of it. A task in between
              // lets the close paint first, so the menu goes at once and the
              // row fades in its own time.
              window.setTimeout(() => onToggleEnabled(target.sectionId), 0);
            }, TOGGLE_FLASH_MS);
          }}
        >
          {isEnabled ? (
            // The struck-through eye, the same pair the timeline's track
            // labels use to hide and show a track — an eye that reads as an
            // eye, rather than the lash-arc `EyeOffIcon` it replaced.
            <EyeClosedIcon strokeColor="currentColor" />
          ) : (
            <EyeOpenIcon strokeColor="currentColor" />
          )}
          {isEnabled ? 'Turn Clip Off' : 'Turn Clip On'}
        </ToggleItem>
      )}
      {onRemove && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          $danger
          $disabled={!canRemove}
          // Still hoverable while disabled, so the native tooltip can say why.
          title={canRemove ? undefined : removeDisabledReason}
          aria-disabled={!canRemove || undefined}
          data-testid="reel-section-menu-remove"
          onClick={() => {
            if (!canRemove) return;
            pick(() => onRemove(target.sectionId));
          }}
        >
          <DeleteIcon />
          Remove from Reel
        </ContextMenuItem>
      )}
    </ContextMenuSurface>,
    document.body,
  );
};

/**
 * The one item whose effect is behind the menu rather than in front of it, so
 * the one that acknowledges its own click: gold — the accent the design system
 * reserves for visual controls — held for {@link TOGGLE_FLASH_MS}. Everything
 * else here closes onto a change the creator can already see (a picker opening,
 * a row leaving), and stays plain.
 */
const ToggleItem = styled(ContextMenuItem)<{ $picked?: boolean }>`
  ${props =>
    props.$picked &&
    css`
      /* No transition on the way in: the item's own 120ms background fade is
         as long as the flash itself, so easing into gold would show a wash
         rather than a click. It has to land with the pointer. */
      transition: none;
      background: var(--accent);
      color: var(--background);
    `}
`;
